// Fetches the owner of a generated birthday row and renders its age + year
// table (profile spec §3.5).
//
// Split out of the detail route so the route file does not grow a second data
// concern, and so the "no profile yet" case has somewhere real to land: a
// birthday row can outlive the profile behind it (a dissolved pair's rows are
// kept for 7 days), and this renders nothing rather than a card with holes in
// it.

import { useQuery } from "@tanstack/react-query";
import { BirthdayAgeCard } from "@/components/profile/BirthdayAgeCard";
import { Card } from "@/components/ui/card";
import { getMyProfile, getPartnerProfile, type ProfileRead } from "@/lib/profile/api";
import { usePartnerId } from "@/lib/profile/usePartnerId";
import type { AnniversaryRead } from "@/lib/api/anniversary";

export function BirthdayAgeSlot({ anniversary }: { anniversary: AnniversaryRead }) {
  const partnerId = usePartnerId();

  const { data } = useQuery({
    queryKey: ["profile", "birthday-owner", anniversary.id],
    queryFn: async (): Promise<ProfileRead | null> => {
      // The row's author is the member it was generated for; when that is the
      // caller (the person looking at their own birthday) use /me.
      if (anniversary.author?.id) {
        try {
          return await getPartnerProfile(anniversary.author.id);
        } catch {
          // Not a pair member any more — fall through to the caller's own.
        }
      }
      if (partnerId && anniversary.author?.id === partnerId) {
        return getPartnerProfile(partnerId);
      }
      try {
        return await getMyProfile();
      } catch {
        return null;
      }
    },
    enabled: Boolean(anniversary.date),
    staleTime: 60_000,
  });

  if (!data) return null;

  // W53.2 D-6 — the spec's §3.3.3 said 「同樣處理」, i.e. make this a hero too,
  // and **that is not done here on purpose**. This renders *below* the page's
  // hero (`[id].tsx:128` then `:134`), so a second `HeroCard` would put a second
  // mascot on one screen — F16's 「每屏恰好 1 块」 broken in the direction of
  // two, and G-6 counts `data-hero-card` to prove it.
  //
  // It is also not a hero by content: an age and a year table is data, not the
  // screen's emotional peak, which the title and countdown above it already is.
  //
  // So it becomes an ordinary L2 card — `surface-plain` + `border-hairline` +
  // `shadow-card` + `rounded-2xl`, the contract every other detail-page card now
  // meets. That also removes the last `rounded-xl` on this screen without
  // inventing a second peak.
  return (
    <Card className="p-5">
      <BirthdayAgeCard birthday={data.birthday} joinedAt={data.created_at} />
    </Card>
  );
}
